import { z } from 'zod'
import { tenantFeatureSchema } from '../enums/tenant-feature.enum.js'
import { billingOwnerDtoSchema } from './billing-owner.dto.js'
import { billingSummaryDtoSchema } from './billing-summary.dto.js'

/**
 * The caller's own organization, as its people see it: what it is called and
 * what it has. Not `TenantDto`, which is the super admin's view of every
 * organization and carries counts nobody inside one needs.
 *
 * This is where the web app learns which features to draw, and where billing
 * state will be read from as well.
 */
export const organizationDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** The features switched on for this organization. Absent from the list is off. */
  features: z.array(tenantFeatureSchema),
  /**
   * Who pays for it. Null only for an organization that existed before billing
   * and has not had one chosen yet.
   */
  billingOwner: billingOwnerDtoSchema.nullable(),
  billing: billingSummaryDtoSchema,
  /**
   * What the billing owner still has to do before paying — the steps between an
   * unfinished signup (or a company created by hand) and Checkout. Null for
   * anyone but the owner.
   */
  ownerTasks: z
    .object({
      /** Accept the terms of service and privacy policy in force. */
      acceptLegal: z.boolean(),
      /** Create the company's first venue. */
      createVenue: z.boolean(),
    })
    .nullable(),
})

export type OrganizationDto = z.infer<typeof organizationDtoSchema>
